import React, { useEffect, useMemo, useState } from 'react';
import EnrolmentApprovalPanel from '../therapist/EnrolmentApprovalPanel';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, CircularProgress, Alert, Divider, Chip,
  ToggleButton, ToggleButtonGroup, TextField, Menu, MenuItem, Snackbar
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import {
  getCallTranscript,
  runTranscriptCleanup,
  saveEditedTranscript
} from '../../services/callsApi';
import { wordDiff } from '../../utils/wordDiff';

function formatTime(ts) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return ''; }
}
function formatDuration(sec) {
  if (sec == null) return '';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}
function confidenceTone(c) {
  if (typeof c !== 'number') return 'default';
  if (c >= 0.85) return 'success';
  if (c >= 0.65) return 'warning';
  return 'error';
}
function formatConfidence(c) {
  if (typeof c !== 'number') return '—';
  return `${Math.round(c * 100)}%`;
}
function speakerLabel(role) {
  return role === 'therapist' ? 'Therapist' : role === 'client' ? 'Client' : 'Speaker';
}
function speakerColor(role) {
  return role === 'therapist' ? 'primary.main' : role === 'client' ? 'secondary.main' : 'text.primary';
}

// --- Segment list renderers ----------------------------------------------

function SegmentRow({ msg, children, showConfidence = true }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: speakerColor(msg.speakerRole) }}>
          {speakerLabel(msg.speakerRole)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatTime(msg.timestamp)}
        </Typography>
        {showConfidence && typeof msg.confidence === 'number' && (
          <Chip
            label={formatConfidence(msg.confidence)}
            size="small"
            variant="outlined"
            color={confidenceTone(msg.confidence)}
            sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
          />
        )}
      </Box>
      {children}
    </Box>
  );
}

function PlainTranscript({ messages }) {
  if (!messages || messages.length === 0) {
    return <Alert severity="info">No segments to show.</Alert>;
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {messages.map((m, i) => (
        <SegmentRow key={i} msg={m}>
          <Typography variant="body2">{m.text}</Typography>
        </SegmentRow>
      ))}
    </Box>
  );
}

function DiffSpan({ part }) {
  if (part.type === 'same') return <span>{part.text}</span>;
  if (part.type === 'added') {
    return (
      <span style={{
        textDecoration: 'underline',
        textDecorationColor: '#2e7d32',
        textDecorationThickness: '2px',
        backgroundColor: 'rgba(46,125,50,0.10)',
        color: '#1b5e20'
      }}>{part.text}</span>
    );
  }
  // removed
  return (
    <span style={{
      textDecoration: 'line-through',
      textDecorationColor: '#c62828',
      color: '#c62828',
      backgroundColor: 'rgba(198,40,40,0.08)'
    }}>{part.text}</span>
  );
}

function DiffTranscript({ rawMessages, cleanedMessages }) {
  if (!cleanedMessages || cleanedMessages.length === 0) {
    return <Alert severity="info">Cleaned version required to view diff.</Alert>;
  }
  if (!rawMessages || rawMessages.length === 0) {
    return <Alert severity="info">No raw messages to diff against.</Alert>;
  }
  const n = Math.max(rawMessages.length, cleanedMessages.length);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="caption" color="text.secondary">
        <span style={{ backgroundColor: 'rgba(198,40,40,0.08)', color: '#c62828', textDecoration: 'line-through', textDecorationColor: '#c62828' }}>removed</span>
        {'  '}
        <span style={{ backgroundColor: 'rgba(46,125,50,0.10)', color: '#1b5e20', textDecoration: 'underline', textDecorationThickness: '2px', textDecorationColor: '#2e7d32' }}>added</span>
      </Typography>
      {Array.from({ length: n }).map((_, i) => {
        const raw = rawMessages[i];
        const cleaned = cleanedMessages[i];
        if (!cleaned) {
          return (
            <SegmentRow key={i} msg={raw} showConfidence={false}>
              <Typography variant="body2"><DiffSpan part={{ type: 'removed', text: raw.text }} /></Typography>
            </SegmentRow>
          );
        }
        if (!raw) {
          return (
            <SegmentRow key={i} msg={cleaned} showConfidence={false}>
              <Typography variant="body2"><DiffSpan part={{ type: 'added', text: cleaned.text }} /></Typography>
            </SegmentRow>
          );
        }
        const parts = wordDiff(raw.text, cleaned.text);
        const changed = parts.some(p => p.type !== 'same');
        return (
          <SegmentRow key={i} msg={cleaned} showConfidence={!changed ? true : false}>
            <Typography variant="body2">
              {parts.map((p, k) => <DiffSpan key={k} part={p} />)}
            </Typography>
          </SegmentRow>
        );
      })}
    </Box>
  );
}

function EditableTranscript({ messages, onChange }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {messages.map((m, i) => (
        <Box key={i} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: speakerColor(m.speakerRole) }}>
              {speakerLabel(m.speakerRole)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatTime(m.timestamp)}
            </Typography>
          </Box>
          <TextField
            value={m.text}
            onChange={(e) => onChange(i, e.target.value)}
            multiline
            size="small"
            fullWidth
            sx={{ '& .MuiInputBase-input': { fontSize: 14 } }}
          />
        </Box>
      ))}
    </Box>
  );
}

// --- Main dialog ----------------------------------------------------------

export default function TranscriptReviewDialog({ open, onClose, token, liveCallId, callMeta }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [copied, setCopied] = useState(false);
  const [view, setView] = useState('raw'); // 'raw' | 'diff' | 'cleaned' | 'edited'
  const [cleaning, setCleaning] = useState(false);
  const [cleanupError, setCleanupError] = useState(null);

  // Edit-mode state
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState(null); // array of messages being edited
  const [editSource, setEditSource] = useState(null); // 'raw' | 'cleaned' | 'edited'
  const [editAnchor, setEditAnchor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedToast, setSavedToast] = useState(false);

  const rawMessages = useMemo(
    () => (transcript?.messages || []).filter(m => m.isFinal),
    [transcript]
  );
  const cleanedMessages = transcript?.cleanedMessages || [];
  const editedMessages = transcript?.editedMessages || [];
  const hasCleaned = cleanedMessages.length > 0;
  const hasEdited  = editedMessages.length > 0;

  useEffect(() => {
    if (!open || !liveCallId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setTranscript(null);
    setCopied(false);
    setCleanupError(null);
    setEditing(false);
    setEditDraft(null);
    setEditSource(null);
    (async () => {
      try {
        const data = await getCallTranscript({ token, liveCallId });
        if (cancelled) return;
        setTranscript(data);
        // Default to the most-refined version available.
        if ((data?.editedMessages || []).length > 0) setView('edited');
        else if ((data?.cleanedMessages || []).length > 0) setView('cleaned');
        else setView('raw');
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        setError(
          status === 404
            ? 'No transcript was captured for this call yet.'
            : err?.response?.data?.msg || 'Failed to load transcript'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, liveCallId, token]);

  const handleClean = async () => {
    setCleaning(true);
    setCleanupError(null);
    try {
      const { transcript: updated } = await runTranscriptCleanup({ token, liveCallId });
      setTranscript(updated);
      setView('cleaned');
    } catch (err) {
      setCleanupError(err?.response?.data?.msg || err.message || 'Cleanup failed');
    } finally {
      setCleaning(false);
    }
  };

  const startEdit = (source) => {
    setEditAnchor(null);
    const base = source === 'edited' ? editedMessages
               : source === 'cleaned' ? cleanedMessages
               : rawMessages;
    // Deep-copy so in-progress edits don't mutate the loaded transcript.
    setEditDraft(base.map(m => ({
      speaker:     m.speaker,
      speakerRole: m.speakerRole,
      text:        m.text,
      isFinal:     true,
      confidence:  m.confidence ?? null,
      timestamp:   m.timestamp
    })));
    setEditSource(source);
    setEditing(true);
  };

  const cancelEdit = () => {
    if (editDraft && window.confirm('Discard your unsaved edits?')) {
      setEditing(false);
      setEditDraft(null);
      setEditSource(null);
    } else if (!editDraft) {
      setEditing(false);
    }
  };

  const saveEdit = async () => {
    if (!editDraft || !editSource) return;
    setSaving(true);
    try {
      const { transcript: updated } = await saveEditedTranscript({
        token, liveCallId,
        messages: editDraft,
        source: editSource
      });
      setTranscript(updated);
      setEditing(false);
      setEditDraft(null);
      setEditSource(null);
      setView('edited');
      setSavedToast(true);
    } catch (err) {
      setCleanupError(err?.response?.data?.msg || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    const list = view === 'edited' ? editedMessages
               : view === 'cleaned' ? cleanedMessages
               : rawMessages;
    const text = list
      .map(m => `[${formatTime(m.timestamp)}] ${m.speakerRole || 'speaker'}: ${m.text}`)
      .join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const cleanedInfo = transcript?.cleanedAt
    ? `cleaned ${new Date(transcript.cleanedAt).toLocaleString()}${transcript.cleanedBy ? ` · ${transcript.cleanedBy}` : ''}`
    : null;
  const editedInfo = transcript?.editedAt
    ? `edited ${new Date(transcript.editedAt).toLocaleString()}${transcript.editedSource ? ` · from ${transcript.editedSource}` : ''}`
    : null;

  const currentView = editing ? 'editing' : view;

  return (
    <>
      <Dialog open={open} onClose={editing ? undefined : onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editing ? 'Edit transcript' : 'Call transcript'}
          {callMeta?.createdAt && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {new Date(callMeta.createdAt).toLocaleString()}
              {callMeta.durationSec != null && ` · ${formatDuration(callMeta.durationSec)}`}
              {transcript?.provider && ` · STT: ${transcript.provider}`}
              {editing && editSource && ` · editing from ${editSource}`}
            </Typography>
          )}
        </DialogTitle>
        <Divider />

        {!editing && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, pt: 2, pb: 1, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(_, v) => v && setView(v)}
            >
              <ToggleButton value="raw">Raw</ToggleButton>
              <ToggleButton value="diff" disabled={!hasCleaned}>Diff</ToggleButton>
              <ToggleButton value="cleaned" disabled={!hasCleaned}>Cleaned</ToggleButton>
              <ToggleButton value="edited" disabled={!hasEdited}>Edited</ToggleButton>
            </ToggleButtonGroup>
            {!hasCleaned && (
              <Button
                size="small"
                startIcon={cleaning ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
                onClick={handleClean}
                disabled={cleaning || rawMessages.length === 0}
              >
                {cleaning ? 'Cleaning…' : 'Clean up with AI'}
              </Button>
            )}
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={(e) => setEditAnchor(e.currentTarget)}
              disabled={rawMessages.length === 0}
            >
              Edit
            </Button>
            <Menu
              anchorEl={editAnchor}
              open={!!editAnchor}
              onClose={() => setEditAnchor(null)}
            >
              <MenuItem onClick={() => startEdit('raw')}>Start from Raw</MenuItem>
              <MenuItem onClick={() => startEdit('cleaned')} disabled={!hasCleaned}>
                Start from Cleaned
              </MenuItem>
              <MenuItem onClick={() => startEdit('edited')} disabled={!hasEdited}>
                Continue editing
              </MenuItem>
            </Menu>
            <Box sx={{ flex: 1 }} />
            {view === 'cleaned' && cleanedInfo && (
              <Typography variant="caption" color="text.secondary">{cleanedInfo}</Typography>
            )}
            {view === 'edited' && editedInfo && (
              <Typography variant="caption" color="text.secondary">{editedInfo}</Typography>
            )}
          </Box>
        )}

        {cleanupError && (
          <Alert severity="error" sx={{ mx: 3, mb: 1 }}>{cleanupError}</Alert>
        )}

        <DialogContent sx={{ minHeight: 320, maxHeight: '60vh' }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <CircularProgress />
            </Box>
          )}
          {!loading && error && <Alert severity="info">{error}</Alert>}
          {!loading && !error && editing && editDraft && (
            <EditableTranscript
              messages={editDraft}
              onChange={(i, text) => setEditDraft(prev => {
                const next = prev.slice();
                next[i] = { ...next[i], text };
                return next;
              })}
            />
          )}
          {!loading && !error && !editing && view === 'raw'     && <PlainTranscript messages={rawMessages} />}
          {!loading && !error && !editing && view === 'cleaned' && <PlainTranscript messages={cleanedMessages} />}
          {!loading && !error && !editing && view === 'edited'  && <PlainTranscript messages={editedMessages} />}
          {!loading && !error && !editing && view === 'diff'    && (
            <DiffTranscript rawMessages={rawMessages} cleanedMessages={cleanedMessages} />
          )}
        </DialogContent>

        {!editing && (callMeta?.clientId?._id || callMeta?.clientId) && (
          <Box sx={{ px: 3, pb: 1 }}>
            <EnrolmentApprovalPanel
              clientId={callMeta.clientId?._id || callMeta.clientId}
              liveCallId={liveCallId}
              token={token}
            />
          </Box>
        )}

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {editing ? (
            <>
              <Button onClick={cancelEdit} startIcon={<CloseIcon />} disabled={saving}>Cancel</Button>
              <Box sx={{ flex: 1 }} />
              <Button
                onClick={saveEdit}
                startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
                variant="contained"
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save edits'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCopy} startIcon={<ContentCopyIcon />} size="small">
                {copied ? 'Copied' : 'Copy transcript'}
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button onClick={onClose}>Close</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={savedToast}
        autoHideDuration={2500}
        onClose={() => setSavedToast(false)}
        message="Edits saved"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
